// src/lib/supabase.ts
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import {
  IS_DEMO_MODE,
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  hasSupabaseCredentials,
} from '../config/env';

// Re-export IS_DEMO_MODE for backward compatibility
// Components importing isDemoMode from this file will continue to work
export const isDemoMode = IS_DEMO_MODE;

/**
 * Returned only outside explicit demo mode when the application has no usable
 * backend configuration. It makes accidental service calls fail closed instead
 * of falling through to local sample data.
 */
export const BACKEND_UNAVAILABLE_MESSAGE =
  'AX1 backend is unavailable. Configure Supabase before using this environment.';

const unavailableBackend = new Proxy({} as SupabaseClient, {
  get() {
    throw new Error(BACKEND_UNAVAILABLE_MESSAGE);
  },
});

/**
 * Supabase client instance.
 * 
 * - In DEMO mode: null (no Supabase connection)
 * - In other modes: Configured SupabaseClient instance
 * 
 * All code using this client MUST check for null before calling methods.
 * Example: if (supabase) { await supabase.from('table').select('*'); }
 */
// ── Debug: confirm env vars reached the client ────────────────────────────
if (typeof window !== 'undefined') {
  console.debug('[supabase.ts] IS_DEMO_MODE:', IS_DEMO_MODE);
  console.debug('[supabase.ts] hasSupabaseCredentials:', hasSupabaseCredentials);
  console.debug('[supabase.ts] SUPABASE_URL:', SUPABASE_URL || '(empty)');
  console.debug('[supabase.ts] publishable key present:', Boolean(SUPABASE_PUBLISHABLE_KEY));
}
// ────────────────────────────────────────────────────────────────────────────

export const supabase: SupabaseClient = IS_DEMO_MODE
  ? null as unknown as SupabaseClient
  : hasSupabaseCredentials
    ? createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  })
    : unavailableBackend;

// Log demo mode status (only in non-test environments)
if (IS_DEMO_MODE) {
  if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'test') {
    console.warn('Running in demo mode - Supabase client is disabled. Using localStorage and mock data.');
  }
}

export const demoNotice = IS_DEMO_MODE ? 'Demo mode: connectivity disabled, showing sample data only.' : '';

// Database types
export type Database = Record<string, never>;

// Jest test helper stubs to satisfy imports expecting RPC mock controls
const __supabaseRpcResponses: Record<string, unknown> = {};
export function __setSupabaseRpcResponse(name: string, value: unknown) { __supabaseRpcResponses[name] = value; }
export function __getSupabaseRpcResponse(name?: string) { return name ? __supabaseRpcResponses[name] : __supabaseRpcResponses; }
